import {
    Badge,
    Box,
    Button,
    Checkbox,
    Container,
    Divider,
    Group,
    LoadingOverlay,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Switch,
    Table,
    Tabs,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import {
    getUserRoutes,
    getUserVerticals,
    getVerticals,
} from '../../../../../http/apiClient';
import { useEffect, useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SearchableSelect } from '../../../../common';
import { TimePicker } from '@mantine/dates';
import dayjs from 'dayjs';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { hasRole } from '../../../../../utils/roles';
import { useAuthStore } from '../../../../../store';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';

export const DAY_DEFS = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
];

const ScheduleTable = ({ form }) => {
    /* ---------- misc ---------- */
    const renderDayRow = ({ key, label }) => {
        const enabledPath = `${key}.enabled`;
        const startPath = `${key}.startTime`;
        const endPath = `${key}.endTime`;

        const enabled = form.getInputProps(enabledPath).value;

        useEffect(() => {
            form.validate(endPath);
            form.validate(startPath);
        }, [form.values?.[key], form.errors?.[key]]);

        return (
            <Table.Tr key={key}>
                {/* Day label */}
                <Table.Td>
                    <Text>{label}</Text>
                </Table.Td>

                {/* Toggle */}
                <Table.Td>
                    <Switch
                        size='xs'
                        {...form.getInputProps(enabledPath, {
                            type: 'checkbox',
                        })}
                    />
                </Table.Td>

                {/* Start time */}
                <Table.Td>
                    <TimePicker
                        disabled={!enabled}
                        radius='md'
                        size={'xs'}
                        withDropdown
                        format='12h'
                        hoursStep={1}
                        minutesStep={5}
                        key={form.key(startPath)}
                        {...form.getInputProps(startPath)}
                    />
                </Table.Td>

                {/* End time */}
                <Table.Td>
                    <TimePicker
                        disabled={!enabled}
                        radius='md'
                        size={'xs'}
                        withDropdown
                        format='12h'
                        hoursStep={1}
                        minutesStep={5}
                        key={form.key(endPath)}
                        {...form.getInputProps(endPath)}
                    />
                </Table.Td>
            </Table.Tr>
        );
    };

    /* ---------- ui ---------- */
    return (
        <>
            <Table>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th align={'left'}>Day</Table.Th>
                        <Table.Th align={'left'}>Enabled</Table.Th>
                        <Table.Th>Start</Table.Th>
                        <Table.Th>End</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{DAY_DEFS.map(renderDayRow)}</Table.Tbody>
            </Table>
        </>
    );
};
export const DEFAULT_SCHEDULE = {
    enabled: true,
    startTime: '08:00',
    endTime: '17:00',
};
const ScheduleModal = ({ onChange, existingSchedule, opened, close }) => {
    const getInitialValues = () => {
        return DAY_DEFS.reduce((acc, { key }) => {
            acc[key] = {
                ...DEFAULT_SCHEDULE,
                ...(existingSchedule ? existingSchedule[key] : {}),
            };
            return acc;
        }, {});
    };
    const form = useForm({
        validateInputOnBlur: true,
        validateInputOnChange: true,
        clearInputErrorOnChange: true,
        initialValues: getInitialValues(),

        validate: DAY_DEFS.reduce((rules, { key }) => {
            rules[`${key}.startTime`] = (value, values) => {
                const d = values[key];
                const ret =
                    d.enabled &&
                    dayjs(value, 'HH:mm').isAfter(dayjs(d.end, 'HH:mm'))
                        ? 'Start must be earlier than end'
                        : null;

                return ret;
            };

            rules[`${key}.endTime`] = (value, values) => {
                const d = values[key];
                const ret =
                    d.enabled &&
                    dayjs(value, 'HH:mm').isBefore(dayjs(d.start, 'HH:mm'))
                        ? 'End must be later than start'
                        : null;

                return ret;
            };
            return rules;
        }, {}),
    });

    useEffect(() => {
        if (existingSchedule) {
            const initialValues = getInitialValues();
            form.setInitialValues(initialValues);
            form.reset();
        }
    }, [opened, existingSchedule]);

    const onSubmit = (values, e) => {
        e.stopPropagation();
        onChange(values);
        close();
    };

    return (
        <Modal.Root
            opened={opened}
            onClose={close}
            size={'md'}>
            <Modal.Overlay />
            <Modal.Content>
                <Modal.Header>
                    <Modal.Title>Schedule</Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={form.onSubmit(onSubmit)}>
                        <Stack>
                            <ScheduleTable
                                form={form}
                                key={form.values}
                            />
                            <Group justify={'flex-end'}>
                                <Button
                                    type={'submit'}
                                    size={'xs'}
                                    disabled={!form.isValid()}>
                                    Save
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};

const ScheduleSubview = ({ form, onChange }) => {
    const [scheduleModalOpen, { open: openModal, close }] = useDisclosure();

    const handleScheduleChange = (values) => {
        form.setFieldValue('scheduleOptions', values);
    };

    return (
        <>
            <Group>
                <Checkbox
                    label={'Enable Schedule'}
                    key={form.key('scheduleEnabled')}
                    {...form.getInputProps('scheduleEnabled')}
                    checked={form.values.scheduleEnabled}
                />
                <Button
                    size={'compact-xs'}
                    onClick={openModal}
                    disabled={!form.values.scheduleEnabled}>
                    Change Schedule
                </Button>
            </Group>
            <ScheduleModal
                existingSchedule={form.values.scheduleOptions}
                onChange={handleScheduleChange}
                opened={scheduleModalOpen}
                close={close}
            />
        </>
    );
};

export default ScheduleSubview;
